// src/components/common/CustomDropdown.jsx
"use client";

import { useState, useRef, useEffect } from 'react';
import './CustomDropdown.css';

/**
 * Styled dropdown replacing native <select>.
 * variant="pill"  — compact filter-bar pill (adoption feed)
 * variant="field" — full-width form field (add/edit pet forms)
 */
const CustomDropdown = ({
  label,
  value,
  options,
  anyLabel = 'Any',
  showAnyOption = true,
  placeholder = 'Select...',
  onChange,
  variant = 'pill',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const UNSET_VALUES = showAnyOption ? ['any'] : ['any', ''];
  const isUnset = value == null || UNSET_VALUES.includes(value);
  const currentLabel = !isUnset
    ? options.find((opt) => opt.value === value)?.label || value
    : null;

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
  };

  const triggerClass = [
    'custom-dropdown-trigger',
    `custom-dropdown-trigger--${variant}`,
    open ? 'open' : '',
    !isUnset ? 'has-value' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={rootRef}
      className={`custom-dropdown custom-dropdown--${variant}`}
    >
      <button
        type="button"
        className={triggerClass}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        {variant === 'pill' && label && (
          <span className="custom-dropdown-label">{label}</span>
        )}
        <span className={`custom-dropdown-value ${isUnset ? 'is-placeholder' : ''}`}>
          {isUnset
            ? (showAnyOption ? anyLabel : placeholder)
            : currentLabel}
        </span>
        <svg
          className="custom-dropdown-chevron"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9L12 15L18 9" />
        </svg>
      </button>

      {open && (
        <div className="custom-dropdown-popover" role="listbox">
          {showAnyOption && (
            <button
              type="button"
              role="option"
              aria-selected={value === 'any'}
              className={`custom-dropdown-option ${value === 'any' ? 'selected' : ''}`}
              onClick={() => handleSelect('any')}
            >
              <span>{anyLabel}</span>
              {value === 'any' && <CheckIcon />}
            </button>
          )}
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={value === opt.value}
              className={`custom-dropdown-option ${value === opt.value ? 'selected' : ''}`}
              onClick={() => handleSelect(opt.value)}
            >
              <span>{opt.label}</span>
              {value === opt.value && <CheckIcon />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

export default CustomDropdown;
